import {
  Directive,
  ElementRef,
  Renderer2,
  Input,
  OnInit,
  AfterViewChecked,
} from '@angular/core';

const inputs = ['fxFlex', 'fxLayout', 'fxFlexFill'];
const selector = `
  [fxFlex], [fxLayout], [fxFlexFill]
`;

@Directive({
  selector,
  inputs,
  standalone: true,
})
export class CalendarMyFlexLayoutDirective implements OnInit, AfterViewChecked {
  public flexDirection: string | undefined;
  public flexFlow: string | undefined;

  @Input() fxFlexFill: string | undefined;
  @Input() fxFlex: string | undefined;
  @Input() fxLayout: string | undefined;
  @Input('fxLayout.xs') fxLayoutXs: string | undefined;
  @Input('fxLayout.sm') fxLayoutSm: string | undefined;
  @Input('fxLayout.md') fxLayoutMd: string | undefined;
  @Input('fxLayout.lg') fxLayoutLg: string | undefined;
  @Input('fxLayout.lt-sm') fxLayoutLtSm: string | undefined;
  @Input('fxLayout.lt-md') fxLayoutLtMd: string | undefined;
  @Input('fxLayout.lt-lg') fxLayoutLtLg: string | undefined;
  @Input('fxLayout.gt-sm') fxLayoutGtSm: string | undefined;
  @Input('fxLayout.gt-md') fxLayoutGtMd: string | undefined;
  @Input('fxLayout.gt-lg') fxLayoutGtLg: string | undefined;
  @Input() fxLayoutAlign: string | undefined;
  @Input() fxLayoutGap: string | undefined;
  @Input() fxGrow: string | undefined;
  @Input() debugIt: boolean = false;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    if (this.fxLayoutGap) {
      this.renderer.setAttribute(
        this.el.nativeElement,
        'ng-reflect-fx-gap',
        this.fxLayoutGap
      );
    }
    if (this.fxLayoutAlign) {
      this.renderer.setAttribute(
        this.el.nativeElement,
        'ng-reflect-fx-align',
        this.fxLayoutAlign
      );
    }
    if (this.fxGrow) {
      this.renderer.setAttribute(
        this.el.nativeElement,
        'ng-reflect-fx-grow',
        this.fxGrow
      );
    }
    if (this.fxLayout) {
      this.renderer.setAttribute(
        this.el.nativeElement,
        'ng-reflect-fx-layout',
        this.fxLayout
      );
    }
    if (this.fxFlex) {
      this.renderer.setAttribute(
        this.el.nativeElement,
        'ng-reflect-fx-flex',
        this.fxFlex
      );
    }
    if (this.el.nativeElement.hasAttribute('fxFlexFill')) {
      this.renderer.setStyle(this.el.nativeElement, 'height', '100%');
      this.renderer.setStyle(this.el.nativeElement, 'min-height', '100%');
      this.renderer.setStyle(this.el.nativeElement, 'min-width', '100%');
      this.renderer.setStyle(this.el.nativeElement, 'width', '100%');
    }
    if (
      this.el.nativeElement.hasAttribute('fxFlex') ||
      this.el.nativeElement.hasAttribute('fxLayout')
    ) {
      this.applyFlexStyles();
    }
  }

  ngAfterViewChecked(): void {
    if (
      this.el.nativeElement.hasAttribute('fxFlex') ||
      this.el.nativeElement.hasAttribute('fxLayout')
    ) {
      this.ApplyfxLayoutGap();
      this.ApplyfxLayoutFlex();
    }
  }

  private applyFlexStyles(): void {
    this.flexDirection = this.getFlowDirection(this.fxLayout);
    this.flexFlow = this.getFlow(this.fxLayout);

    const alignItems = this.getAlignItems();
    const placeContent = this.getPlaceContent();

    if (this.fxLayout) {
      this.renderer.setStyle(this.el.nativeElement, 'display', 'flex');
      this.renderer.setStyle(
        this.el.nativeElement,
        'flex-direction',
        this.fxLayout
      );
      this.updateLayoutDirection();
    }

    window.addEventListener('resize', () => {
      if (this.fxLayout) {
        this.updateLayoutDirection();
      }
      this.ApplyfxLayoutGap();
      this.ApplyfxLayoutFlex();
    });

    if (placeContent) {
      this.renderer.setStyle(this.el.nativeElement, 'display', 'flex');
      let alignItemsData = 'stretch';
      if (alignItems) {
        if (alignItems === 'center') {
          alignItemsData = 'center';
        } else if (alignItems === 'end') {
          alignItemsData = 'flex-end';
        }
      }
      if (placeContent === 'end') {
        this.renderer.setStyle(
          this.el.nativeElement,
          'place-content',
          alignItemsData + ' flex-end'
        );
        this.renderer.setStyle(this.el.nativeElement, 'align-items', 'stretch');
      } else if (placeContent === 'start') {
        this.renderer.setStyle(
          this.el.nativeElement,
          'place-content',
          alignItemsData + ' flex-start'
        );
        this.renderer.setStyle(
          this.el.nativeElement,
          'align-items',
          'flex-start'
        );
      } else if (placeContent === 'space-between') {
        this.renderer.setStyle(
          this.el.nativeElement,
          'place-content',
          alignItemsData + ' space-between'
        );
        this.renderer.setStyle(this.el.nativeElement, 'align-items', 'stretch');
      } else {
        this.renderer.setStyle(
          this.el.nativeElement,
          'place-content',
          alignItemsData + ' center'
        );
        this.renderer.setStyle(this.el.nativeElement, 'align-items', 'stretch');
      }
    }
    if (alignItems) {
      if (alignItems === 'end') {
        this.renderer.setStyle(
          this.el.nativeElement,
          'align-items',
          'flex-end'
        );
      } else if (alignItems === 'start') {
        this.renderer.setStyle(
          this.el.nativeElement,
          'align-items',
          'flex-start'
        );
      } else if (alignItems === 'space-between') {
        this.renderer.setStyle(
          this.el.nativeElement,
          'align-items',
          'stretch'
        );
      } else if (alignItems === 'center') {
        this.renderer.setStyle(
          this.el.nativeElement,
          'align-items',
          'center'
        );
      } else {
        this.renderer.setStyle(
          this.el.nativeElement,
          'align-items',
          'stretch'
        );
      }
    }
    this.renderer.setStyle(this.el.nativeElement, 'box-sizing', 'border-box');

    let fxGrowData = '1';
    let fxBasisData = '0%';
    if (this.fxGrow !== undefined) {
      fxGrowData = this.fxGrow;
    }
    if (this.fxFlex) {
      if (this.hasCssUnit(this.fxFlex)) {
        // Fixed size (e.g. fxFlex="29px"): don't grow, don't shrink
        this.renderer.setStyle(
          this.el.nativeElement,
          'flex',
          '0 0 ' + this.fxFlex
        );
      } else {
        const siblingBefore = this.el.nativeElement.previousElementSibling;
        const siblingAfter = this.el.nativeElement.nextElementSibling;
        if (!siblingBefore || !siblingAfter) {
          fxBasisData = '100%';
        }
        this.renderer.setStyle(
          this.el.nativeElement,
          'flex',
          fxGrowData + ' 1 ' + fxBasisData
        );
      }
    } else {
      this.renderer.setStyle(
        this.el.nativeElement,
        'flex',
        fxGrowData + ' 1 ' + fxBasisData
      );
    }
    this.ApplyfxLayoutGap();
    this.ApplyfxLayoutFlex();
  }

  private getPlaceContent(): string | undefined {
    let placeContent: string | undefined;
    if (this.fxLayoutAlign) {
      const layoutValues = this.fxLayoutAlign.split(' ');
      placeContent = layoutValues[0] || undefined;
    }
    return placeContent;
  }

  private getAlignItems(): string | undefined {
    let alignItem: string | undefined;
    if (this.fxLayoutAlign) {
      const layoutValues = this.fxLayoutAlign.split(' ');
      alignItem = layoutValues[1] || undefined;
    }
    return alignItem;
  }

  private getFlowDirection(fxLayout: string | undefined): string | undefined {
    let flowDirection: string | undefined;
    if (fxLayout) {
      const layoutValues = fxLayout.split(' ');
      flowDirection = layoutValues[0] || undefined;
    }
    return flowDirection;
  }

  private getFlow(fxLayout: string | undefined): string | undefined {
    let flowItem: string | undefined;
    if (fxLayout) {
      const layoutValues = fxLayout.split(' ');
      flowItem = layoutValues[1] || undefined;
    }
    return flowItem;
  }

  /**
   * Returns true if the value has a CSS unit (px, em, rem, %, etc.).
   * Used to treat fxFlex="29px" as fixed size vs fxFlex="50" as percentage.
   */
  private hasCssUnit(value: string | undefined): boolean {
    if (!value || typeof value !== 'string') return false;
    const trimmed = value.trim();
    return /^\d+(\.\d+)?(px|em|rem|%|vw|vh|ch)$/i.test(trimmed) || trimmed.endsWith('%');
  }

  /**
   * For flex-basis / max-width / max-height: use value as-is if it has a unit,
   * otherwise append '%' for percentage.
   */
  private flexValueWithUnit(value: string | undefined): string | undefined {
    if (!value) return undefined;
    if (this.hasCssUnit(value)) return value;
    return value + '%';
  }

  private ApplyfxLayoutGap(): void {
    const parent = this.el.nativeElement.parentElement;
    if (parent) {
      if (parent.hasAttribute('ng-reflect-fx-gap')) {
        const flexGap = parent.getAttribute('ng-reflect-fx-gap');
        if (flexGap) {
          let fxLayout: string | null = undefined;
          let flexDirection = 'column';
          if (parent.hasAttribute('ng-reflect-fx-layout')) {
            fxLayout = parent.getAttribute('ng-reflect-fx-layout');
            flexDirection = this.getFlowDirection(fxLayout || undefined) || 'column';
          }
          const siblingAfter = this.el.nativeElement.nextElementSibling;
          const flexSize = this.flexValueWithUnit(this.fxFlex);
          if (flexDirection === 'row') {
            if (siblingAfter) {
              this.renderer.setStyle(
                this.el.nativeElement,
                'margin-right',
                flexGap
              );
              this.renderer.removeStyle(this.el.nativeElement, 'margin-bottom');
            }
            if (flexSize) {
              this.renderer.setStyle(
                this.el.nativeElement,
                'max-width',
                flexSize
              );
            }
            this.renderer.removeStyle(this.el.nativeElement, 'max-height');
          } else {
            if (siblingAfter) {
              this.renderer.setStyle(
                this.el.nativeElement,
                'margin-bottom',
                flexGap
              );
              this.renderer.removeStyle(this.el.nativeElement, 'margin-right');
            }
            if (flexSize) {
              this.renderer.setStyle(
                this.el.nativeElement,
                'max-height',
                flexSize
              );
            }
            this.renderer.removeStyle(this.el.nativeElement, 'max-width');
          }
        }
      }
    }
  }

  private ApplyfxLayoutFlex(): void {
    if (this.el.nativeElement.hasAttribute('ng-reflect-fx-flex')) {
      const flexFlex = this.el.nativeElement.getAttribute('ng-reflect-fx-flex');
      if (flexFlex) {
        const flexSize = this.flexValueWithUnit(flexFlex);
        if (!flexSize) return;
        let flexDirection = 'column';
        const parent = this.el.nativeElement.parentElement;
        if (parent && parent.hasAttribute('ng-reflect-fx-layout')) {
          const fxLayout = parent.getAttribute('ng-reflect-fx-layout');
          flexDirection =
            this.getFlowDirection(fxLayout || undefined) || 'column';
        }
        if (this.hasCssUnit(flexFlex)) {
          // Fixed size (e.g. 29px): use flex so the column doesn't grow/shrink
          if (flexDirection === 'row') {
            this.renderer.setStyle(this.el.nativeElement, 'flex', `0 0 ${flexFlex}`);
            this.renderer.setStyle(this.el.nativeElement, 'min-width', flexFlex);
            this.renderer.setStyle(this.el.nativeElement, 'max-width', flexFlex);
            this.renderer.removeStyle(this.el.nativeElement, 'max-height');
          } else {
            this.renderer.setStyle(this.el.nativeElement, 'flex', `0 0 ${flexFlex}`);
            this.renderer.setStyle(this.el.nativeElement, 'min-height', flexFlex);
            this.renderer.setStyle(this.el.nativeElement, 'max-height', flexFlex);
            this.renderer.removeStyle(this.el.nativeElement, 'max-width');
          }
        } else {
          if (flexDirection === 'row') {
            this.renderer.setStyle(
              this.el.nativeElement,
              'max-width',
              flexSize
            );
            this.renderer.removeStyle(this.el.nativeElement, 'max-height');
          } else {
            this.renderer.setStyle(
              this.el.nativeElement,
              'max-height',
              flexSize
            );
            this.renderer.removeStyle(this.el.nativeElement, 'max-width');
          }
        }
      }
    }
  }

  private updateLayoutDirection(): void {
    const screenWidth =
      document?.body?.clientWidth ?? document?.defaultView?.innerWidth ?? window.innerWidth;

    let fxLayout: string | undefined;
    if (screenWidth < 576) {
      fxLayout =
        this.fxLayoutXs ??
        this.fxLayoutSm ??
        this.fxLayoutLtSm ??
        this.fxLayoutGtSm ??
        this.fxLayoutMd ??
        this.fxLayoutLtMd ??
        this.fxLayoutGtMd ??
        this.fxLayout;
    } else if (screenWidth < 960) {
      fxLayout =
        this.fxLayoutMd ??
        this.fxLayoutLtMd ??
        this.fxLayoutGtMd ??
        this.fxLayout;
    } else if (screenWidth >= 1200) {
      fxLayout =
        this.fxLayoutLg ??
        this.fxLayoutLtLg ??
        this.fxLayoutGtLg ??
        this.fxLayout;
    } else {
      fxLayout = this.fxLayout;
    }
    if (fxLayout) {
      const flexDirection = this.getFlowDirection(fxLayout);
      const flexFlow = this.getFlow(fxLayout);
      this.renderer.setStyle(
        this.el.nativeElement,
        'flex-direction',
        flexDirection
      );
      if (flexFlow) {
        this.renderer.setStyle(
          this.el.nativeElement,
          'flex-flow',
          flexFlow
        );
      }
      this.renderer.setAttribute(
        this.el.nativeElement,
        'ng-reflect-fx-layout',
        fxLayout
      );
    }
  }
}
